import dayjs from 'dayjs';
import { ExportToCsv } from 'export-to-csv';

/**
 * Форматирует значение ячейки для CSV (даты, массивы, объекты).
 * @param {unknown} value
 * @returns {string}
 */
function formatCellValue(value) {
  if (value == null) return '';
  if (value instanceof Date) return dayjs(value).format('YYYY-MM-DD');
  if (value && typeof value === 'object' && typeof value.format === 'function') {
    return dayjs(value).format('YYYY-MM-DD');
  }
  if (Array.isArray(value)) {
    return value
      .map((item) => {
        if (item && typeof item === 'object' && (item.firstName != null || item.lastName != null || item.name != null)) {
          return [item.firstName, item.lastName, item.name].filter(Boolean).join(' ').trim() || String(item);
        }
        return String(item);
      })
      .filter(Boolean)
      .join('; ') || '';
  }
  if (typeof value === 'object') {
    try {
      return JSON.stringify(value);
    } catch {
      return '[Object]';
    }
  }
  return String(value);
}

/**
 * Собирает все ключи из массива объектов (объединение полей всех строк).
 * @param {object[]} rows
 * @param {Set<string>} [omitKeys]
 * @returns {string[]}
 */
function getAllKeys(rows, omitKeys) {
  const keySet = new Set();
  rows.forEach((row) => {
    if (row && typeof row === 'object') {
      Object.keys(row)
        .filter((k) => typeof k === 'string' && !(omitKeys && omitKeys.has(k)))
        .forEach((k) => keySet.add(k));
    }
  });
  return Array.from(keySet).sort();
}

/**
 * Возвращает порядок ключей для CSV: сначала preferredKeys, затем остальные (по алфавиту).
 * @param {string[]} allKeysSorted
 * @param {string[]} [preferredKeys]
 * @returns {string[]}
 */
function orderKeys(allKeysSorted, preferredKeys) {
  const preferred = Array.isArray(preferredKeys) ? preferredKeys.filter((k) => typeof k === 'string' && k.length > 0) : [];
  if (preferred.length === 0) return allKeysSorted;

  const keySet = new Set(allKeysSorted);
  const orderedPreferred = preferred.filter((k) => keySet.has(k));
  const preferredSet = new Set(orderedPreferred);
  const rest = allKeysSorted.filter((k) => !preferredSet.has(k));
  return [...orderedPreferred, ...rest];
}

const DEFAULT_CSV_OPTIONS = {
  fieldSeparator: ',',
  decimalSeparator: '.',
  showLabels: true,
  useKeysAsHeaders: true,
};

/**
 * Создаёт экспортер CSV и генерирует файл.
 * @param {object[]} rowData — данные строк (уже отформатированные для CSV)
 * @param {string} filename — имя файла без расширения
 */
function generateCsv(rowData, filename) {
  const csvExporter = new ExportToCsv({
    ...DEFAULT_CSV_OPTIONS,
    filename: String(filename).replace(/\.csv$/i, ''),
  });
  csvExporter.generateCsv(rowData);
}

/**
 * Экспортирует отфильтрованные строки таблицы MRT в CSV.
 * Экспортируются все поля исходных объектов (row.original) из БД.
 * Учитываются все активные фильтры (поиск, чипы, колоночные фильтры).
 * @param {import('material-react-table').MRT_TableInstance} table — инстанс таблицы MRT
 * @param {string} [filename] — имя файла без расширения (например, 'members')
 * @param {{ preferredKeys?: string[], keyMap?: Record<string, string>, omitKeys?: string[] }} [options]
 * @returns {boolean} true если экспорт выполнен, false если нет строк
 */
export function exportTableToCsv(table, filename = 'export', options) {
  const rowModel = table?.getPrePaginationRowModel?.();
  const rows = rowModel?.rows;
  if (!rows || rows.length === 0) {
    return false;
  }

  const originals = rows.map((row) => row.original);
  const omit = new Set(Array.isArray(options?.omitKeys) ? options.omitKeys : []);
  const keyMap = options?.keyMap && typeof options.keyMap === 'object' ? options.keyMap : undefined;
  const mappedKeys = keyMap ? Object.keys(keyMap).filter((k) => typeof k === 'string' && k.length > 0 && !omit.has(k)) : [];

  const keysSorted = getAllKeys(originals, omit);
  const keys = orderKeys(Array.from(new Set([...mappedKeys, ...keysSorted])).sort(), options?.preferredKeys);
  if (keys.length === 0) {
    return false;
  }

  const rowData = originals.map((raw) => {
    const obj = {};
    keys.forEach((key) => {
      const sourceKey = keyMap?.[key] ?? key;
      const value = raw?.[sourceKey];
      obj[key] = formatCellValue(value);
    });
    return obj;
  });

  try {
    generateCsv(rowData, filename);
    return true;
  } catch (err) {
    console.error('CSV export failed:', err);
    return false;
  }
}

/**
 * Экспортирует массив объектов (например, одну запись со страницы деталей) в CSV.
 * @param {object[]} dataRows — массив объектов (все поля попадут в CSV)
 * @param {string} [filename] — имя файла без расширения
 * @param {{ preferredKeys?: string[], keyMap?: Record<string, string>, omitKeys?: string[] }} [options]
 * @returns {boolean} true если экспорт выполнен, false если нет данных
 */
export function exportDataToCsv(dataRows, filename = 'export', options) {
  const rows = Array.isArray(dataRows) ? dataRows : [dataRows];
  const valid = rows.filter((r) => r != null && typeof r === 'object');
  if (valid.length === 0) return false;

  const omit = new Set(Array.isArray(options?.omitKeys) ? options.omitKeys : []);
  const keyMap = options?.keyMap && typeof options.keyMap === 'object' ? options.keyMap : undefined;
  const mappedKeys = keyMap ? Object.keys(keyMap).filter((k) => typeof k === 'string' && k.length > 0 && !omit.has(k)) : [];

  const keysSorted = getAllKeys(valid, omit);
  const keys = orderKeys(Array.from(new Set([...mappedKeys, ...keysSorted])).sort(), options?.preferredKeys);
  if (keys.length === 0) return false;

  const rowData = valid.map((raw) => {
    const obj = {};
    keys.forEach((key) => {
      const sourceKey = keyMap?.[key] ?? key;
      obj[key] = formatCellValue(raw?.[sourceKey]);
    });
    return obj;
  });

  try {
    generateCsv(rowData, filename);
    return true;
  } catch (err) {
    console.error('CSV export failed:', err);
    return false;
  }
}
